import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import { format } from 'date-fns';
import { CalendarDay, InvestmentRecord } from '../types';
import 'react-calendar/dist/Calendar.css';
import './Calendar.css';

interface CalendarProps {
  onDateSelect: (date: Date) => void;
  calendarData: CalendarDay[];
  onMonthChange?: (year: number, month: number) => void;
  onYearChange?: (year: number) => void;
  fetchCalendarData?: (year: number, month: number) => Promise<void> | void;
}

const InvestmentCalendar: React.FC<CalendarProps> = ({ 
  onDateSelect, 
  calendarData, 
  onMonthChange,
  onYearChange,
  fetchCalendarData
}) => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [view, setView] = useState<'month' | 'year' | 'decade' | 'century'>('month');
  const [loadedYears, setLoadedYears] = useState<Set<number>>(new Set());
  const [allCalendarData, setAllCalendarData] = useState<CalendarDay[]>(calendarData);
  
  // calendarData가 변경될 때 allCalendarData에 병합
  useEffect(() => {
    if (calendarData && calendarData.length > 0) {
      setAllCalendarData(prev => {
        // 날짜 키 기준 upsert: 기존 값을 유지하지 않고 최신 조회 결과로 덮어쓴다.
        const byDate = new Map<string, CalendarDay>();
        prev.forEach(day => {
          byDate.set(format(day.date, 'yyyy-MM-dd'), day);
        });
        calendarData.forEach(day => {
          byDate.set(format(day.date, 'yyyy-MM-dd'), day);
        });

        return Array.from(byDate.values()).sort(
          (a, b) => a.date.getTime() - b.date.getTime()
        );
      });
    }
  }, [calendarData]);

  // 월별 변동률 계산 (전달 마지막날 vs 이번달 마지막날)
  const calculateMonthlyProfitRate = (year: number, month: number): number | null => {
    // 이번달의 모든 날짜 중 마지막 거래일 찾기
    const monthDays = allCalendarData
      .filter(day => {
        const dayYear = day.date.getFullYear();
        const dayMonth = day.date.getMonth() + 1;
        return dayYear === year && dayMonth === month && day.canCalculate && day.profitRate !== undefined;
      })
      .sort((a, b) => a.date.getTime() - b.date.getTime()); // 날짜 순 정렬
    
    if (monthDays.length === 0) return null;
    
    // 이번달 마지막날 수익률
    const lastDayOfMonth = monthDays[monthDays.length - 1];
    const lastDayProfitRate = lastDayOfMonth.profitRate;
    if (lastDayProfitRate === undefined) return null;
    
    // 전달 마지막날 수익률 찾기
    let prevMonth = month - 1;
    let prevYear = year;
    if (prevMonth === 0) {
      prevMonth = 12;
      prevYear = year - 1;
    }
    
    const prevMonthDays = allCalendarData
      .filter(day => {
        const dayYear = day.date.getFullYear();
        const dayMonth = day.date.getMonth() + 1;
        return dayYear === prevYear && dayMonth === prevMonth && day.canCalculate && day.profitRate !== undefined;
      })
      .sort((a, b) => a.date.getTime() - b.date.getTime()); // 날짜 순 정렬
    
    if (prevMonthDays.length === 0) return null;
    
    const prevLastDay = prevMonthDays[prevMonthDays.length - 1];
    const prevLastDayProfitRate = prevLastDay.profitRate;
    if (prevLastDayProfitRate === undefined) return null;
    
    // 월별 변동률 = 이번달 마지막날 수익률 - 전달 마지막날 수익률
    return lastDayProfitRate - prevLastDayProfitRate;
  };

  // 연도별 변동률 계산 (전년도 마지막날 vs 이번년도 마지막날)
  const calculateYearlyProfitRate = (year: number): number | null => {
    // 이번년도의 모든 날짜 중 마지막 거래일 찾기 (12월 마지막 거래일)
    const decemberDays = allCalendarData
      .filter(day => {
        const dayYear = day.date.getFullYear();
        return dayYear === year && day.date.getMonth() === 11 && day.canCalculate && day.profitRate !== undefined;
      })
      .sort((a, b) => a.date.getTime() - b.date.getTime()); // 날짜 순 정렬
    
    if (decemberDays.length === 0) return null;
    
    const lastDayOfYear = decemberDays[decemberDays.length - 1];
    const lastDayProfitRate = lastDayOfYear.profitRate;
    if (lastDayProfitRate === undefined) return null;
    
    // 전년도 마지막날 수익률 찾기 (12월 마지막 거래일)
    const prevYear = year - 1;
    const prevDecemberDays = allCalendarData
      .filter(day => {
        const dayYear = day.date.getFullYear();
        return dayYear === prevYear && day.date.getMonth() === 11 && day.canCalculate && day.profitRate !== undefined;
      })
      .sort((a, b) => a.date.getTime() - b.date.getTime()); // 날짜 순 정렬
    
    if (prevDecemberDays.length === 0) return null;
    
    const prevLastDay = prevDecemberDays[prevDecemberDays.length - 1];
    const prevLastDayProfitRate = prevLastDay.profitRate;
    if (prevLastDayProfitRate === undefined) return null;
    
    // 연도별 변동률 = 이번년도 마지막날 수익률 - 전년도 마지막날 수익률
    return lastDayProfitRate - prevLastDayProfitRate;
  };

  const getTileContent = ({ date }: { date: Date }) => {
    // 연도별 view (10년 단위)
    if (view === 'decade' || view === 'century') {
      const year = date.getFullYear();
      const yearlyRate = calculateYearlyProfitRate(year);
      
      if (yearlyRate !== null) {
        const isZero = Math.abs(yearlyRate) < 0.01;
        return (
          <div className="calendar-tile-content">
            <div 
              className={`profit-rate-change ${
                isZero ? 'neutral' : (yearlyRate > 0 ? 'positive' : 'negative')
              }`}
              title={`${year}년 평균 변동률: ${yearlyRate > 0 ? '+' : ''}${yearlyRate.toFixed(2)}%`}
            >
              {isZero ? '0.0%' : `${yearlyRate > 0 ? '+' : ''}${yearlyRate.toFixed(1)}%`}
            </div>
          </div>
        );
      }
      return null;
    }

    // 월별 view
    if (view === 'year') {
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const monthlyRate = calculateMonthlyProfitRate(year, month);
      
      if (monthlyRate !== null) {
        const isZero = Math.abs(monthlyRate) < 0.01;
        return (
          <div className="calendar-tile-content">
            <div 
              className={`profit-rate-change ${
                isZero ? 'neutral' : (monthlyRate > 0 ? 'positive' : 'negative')
              }`}
              title={`${year}년 ${month}월 평균 변동률: ${monthlyRate > 0 ? '+' : ''}${monthlyRate.toFixed(2)}%`}
            >
              {isZero ? '0.0%' : `${monthlyRate > 0 ? '+' : ''}${monthlyRate.toFixed(1)}%`}
            </div>
          </div>
        );
      }
      return null;
    }

    // 일별 view (기존 로직)
    const dateStr = format(date, 'yyyy-MM-dd');
    const dayData = allCalendarData.find(day => {
      const dayStr = format(day.date, 'yyyy-MM-dd');
      return dayStr === dateStr;
    });

    if (!dayData) {
      return null;
    }

    const { profitRateChange, hasInvestment, isHoliday, canCalculate } = dayData;
    
    // 평일 중 공휴일인 경우 (주말은 제외)
    const dayOfWeek = date.getDay(); // 0=일, 1=월, ..., 6=토
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    
    if (isHoliday && !isWeekend && !canCalculate) {
      return (
        <div className="calendar-tile-content">
          <div className="profit-rate-change holiday" title="공휴일 (휴장)">
            휴장
          </div>
        </div>
      );
    }
    
    // 수익률 변동치가 계산 가능한 경우 (보합 포함)
    if (profitRateChange !== undefined && canCalculate) {
      const isZero = Math.abs(profitRateChange) < 0.01; // 0.01% 미만은 보합으로 간주
      
      return (
        <div className="calendar-tile-content">
          <div 
            className={`profit-rate-change ${
              isZero ? 'neutral' : (profitRateChange > 0 ? 'positive' : 'negative')
            }`}
            title={`전날 대비 수익률 변화: ${profitRateChange > 0 ? '+' : ''}${profitRateChange.toFixed(2)}%`}
          >
            {isZero ? '0.0%' : `${profitRateChange > 0 ? '+' : ''}${profitRateChange.toFixed(1)}%`}
          </div>
        </div>
      );
    }
    
    return null;
  };

  const handleDateChange = (value: any) => {
    const selected = Array.isArray(value) ? value[0] : value;
    setSelectedDate(selected);
    onDateSelect(selected);
  };

  // 요일 포맷 함수 (일월화수목금토 순서)
  // react-calendar는 ISO 8601 기준으로 월요일부터 시작하므로
  // formatShortWeekday에서 전달되는 date는 각 요일의 대표 날짜
  const formatShortWeekday = (locale: string | undefined, date: Date) => {
    const weekdayNames = ['일', '월', '화', '수', '목', '금', '토'];
    // JavaScript Date.getDay(): 일요일(0)부터 토요일(6)까지
    return weekdayNames[date.getDay()];
  };

  // 각 타일의 요일 클래스 추가
  const tileClassName = ({ date }: { date: Date }) => {
    const dayOfWeek = date.getDay(); // 0=일, 1=월, ..., 6=토
    return `day-of-week-${dayOfWeek}`;
  };

  // 연도 데이터를 배치로 순차 로드 (서버 부하 방지)
  // 12개 API를 동시 호출하는 대신 3개씩 배치로 로드
  const loadYearDataSequentially = async (year: number) => {
    if (!fetchCalendarData || loadedYears.has(year)) return;
    
    setLoadedYears(prev => new Set(prev).add(year));
    
    // 1월 수익률 계산을 위해 전년 12월도 로드
    const prevYear = year - 1;
    if (!loadedYears.has(prevYear)) {
      try {
        await fetchCalendarData(prevYear, 12);
      } catch (e) {
        console.warn(`${prevYear}년 12월 데이터 로드 실패:`, e);
      }
    }
    
    // 3개월씩 배치로 순차 로드 (동시 3개 제한)
    for (let batch = 0; batch < 4; batch++) {
      const promises: Promise<void>[] = [];
      for (let i = 0; i < 3; i++) {
        const m = batch * 3 + i + 1;
        if (m <= 12) {
          const result = fetchCalendarData(year, m);
          if (result && typeof (result as any).catch === 'function') {
            promises.push((result as Promise<void>).catch((e: any) => {
              console.warn(`${year}년 ${m}월 데이터 로드 실패:`, e);
            }));
          }
        }
      }
      await Promise.all(promises);
    }
  };

  // 달력 월 변경 감지
  const handleActiveStartDateChange = ({ activeStartDate }: { activeStartDate: Date | null }) => {
    if (activeStartDate) {
      const year = activeStartDate.getFullYear();
      const month = activeStartDate.getMonth() + 1;
      
      if (onMonthChange) {
        onMonthChange(year, month);
      }
      
      // 월별 view로 전환할 때 해당 월과 전후 달 데이터 로드
      if (view === 'month' && fetchCalendarData) {
        // 현재 달 데이터 로드
        fetchCalendarData(year, month);
        
        // 전달 데이터 로드
        const prevMonth = month === 1 ? 12 : month - 1;
        const prevYear = month === 1 ? year - 1 : year;
        const prevResult = fetchCalendarData(prevYear, prevMonth);
        if (prevResult && typeof prevResult.catch === 'function') {
          prevResult.catch((err: any) => 
            console.warn(`Failed to load calendar data for ${prevYear}-${prevMonth}:`, err)
          );
        }
        
        // 다음 달 데이터 로드
        const nextMonth = month === 12 ? 1 : month + 1;
        const nextYear = month === 12 ? year + 1 : year;
        const nextResult = fetchCalendarData(nextYear, nextMonth);
        if (nextResult && typeof nextResult.catch === 'function') {
          nextResult.catch((err: any) => 
            console.warn(`Failed to load calendar data for ${nextYear}-${nextMonth}:`, err)
          );
        }
      }
      
      // 연도별 view로 전환할 때 해당 연도의 모든 월 데이터 순차 로드
      if (view === 'year' && !loadedYears.has(year)) {
        loadYearDataSequentially(year);
      }
      
      // 10년별 view로 전환할 때 해당 10년의 모든 연도 데이터 순차 로드
      if ((view === 'decade' || view === 'century') && fetchCalendarData) {
        const startYear = Math.floor(year / 10) * 10;
        const loadDecadeData = async () => {
          for (let y = startYear; y < startYear + 10; y++) {
            if (!loadedYears.has(y)) {
              await loadYearDataSequentially(y);
            }
          }
        };
        loadDecadeData();
      }
    }
  };
  
  // View 변경 감지
  const handleViewChange = (props: { view: 'month' | 'year' | 'decade' | 'century' }) => {
    setView(props.view);
    
    // View 변경 시 필요한 데이터 로드
    if (selectedDate && fetchCalendarData) {
      const year = selectedDate.getFullYear();
      
      if (props.view === 'year' && !loadedYears.has(year)) {
        loadYearDataSequentially(year);
      } else if ((props.view === 'decade' || props.view === 'century')) {
        const startYear = Math.floor(year / 10) * 10;
        const loadDecadeData = async () => {
          for (let y = startYear; y < startYear + 10; y++) {
            if (!loadedYears.has(y)) {
              await loadYearDataSequentially(y);
            }
          }
        };
        loadDecadeData();
      }
    }
  };

  return (
    <div className="investment-calendar">
      <Calendar
        onChange={handleDateChange}
        value={selectedDate}
        tileContent={getTileContent}
        tileClassName={tileClassName}
        className="custom-calendar"
        formatShortWeekday={formatShortWeekday}
        onActiveStartDateChange={handleActiveStartDateChange}
        onViewChange={handleViewChange}
      />
    </div>
  );
};

export default InvestmentCalendar; 